import { ALL_CURRENCIES, formatMoney } from '@kit/shared/currency';

import type { AddressInputsValue } from '~/lib/addresses/address-parts';

export type PurchaseOrderParty = {
  /**
   * The address as the document prints it, one part per line.
   *
   * Everything that renders a party — the preview, the PDF, and the in-app
   * order PDF that reuses this type — reads this string, so it stays the one
   * thing they need. The creator composes it from `addressParts` below.
   */
  address: string;
  /**
   * The address as it was typed, in parts. Optional because the in-app order
   * PDF builds parties from a stored one-line address and has no parts to
   * give.
   */
  addressParts?: AddressInputsValue;
  contact: string;
  email: string;
  /** Printed under the email when the account actually has a phone. */
  phone?: string;
  name: string;
};

export type PurchaseOrderLineItem = {
  description: string;
  hsCode: string;
  id: string;
  quantity: string;
  sku: string;
  unit: string;
  unitPrice: string;
  /**
   * Set on a complimentary line: "Free" or "Free: Extra units".
   * Empty on a paid line. The PDF prints it under the description.
   */
  freeLabel?: string;
};

/** A payment already recorded against the order, as the PO reports it back. */
export type PurchaseOrderPayment = {
  amount: number;
  date: string;
  /** "Deposit", "Balance". What the payment was for. */
  label: string;
  method: string;
};

/** One milestone of the written payment terms, with its amount filled in. */
export type PurchaseOrderPaymentMilestone = {
  amount: number;
  dueWithOrder: boolean;
  label: string;
  percentOfOrder: number;
  trigger: string;
};

export type PurchaseOrder = {
  buyer: PurchaseOrderParty;
  currency: string;
  discount: string;
  incoterm: string;
  lineItems: PurchaseOrderLineItem[];
  notes: string;
  orderDate: string;
  paymentTerms: string;
  poNumber: string;
  /** Numbered revision of an order PDF saved in the workspace. */
  version?: number;
  /** Buyer-facing name used to distinguish this order from its reference. */
  orderName?: string;
  shipByDate: string;
  /** Account orders use this field as the date goods are ready for dispatch. */
  shipByLabel?: string;
  shipTo: PurchaseOrderParty;
  shippingCost: string;
  supplier: PurchaseOrderParty;
  taxPercent: string;
  /** Charges that are not freight, printed only when they are not zero. */
  otherCharges?: string;
  /** Delivery date, separate from ship-by. Blank when the order has none. */
  expectedDeliveryDate?: string;
  /** Packing and labelling notes the supplier is meant to follow. */
  supplierInstructions?: string;
  /** Milestones read from `paymentTerms`. Empty when the words did not parse. */
  paymentSchedule?: PurchaseOrderPaymentMilestone[];
  /** True when `paymentSchedule` is the terms, so the raw text is not repeated. */
  paymentScheduleParsed?: boolean;
  /**
   * False when a deposit figure must not be printed. Undefined keeps the
   * previous rule: print a positive `depositAmount`.
   */
  showDepositSplit?: boolean;
  /** Non-payment sentences that sat beside a schedule, such as a fee note. */
  paymentNotes?: string;
  /**
   * Bank lines already cleared for printing. A hidden extraction is a mask
   * and a note, never the account number.
   */
  paymentDetails?: string[];
  /*
   * Everything below is optional, and blank on the free generator: it has no
   * account behind it and so knows nothing about deposits, payments, or what
   * a supplier calls the order. The app fills them in.
   */
  /**
   * The supplier's own number for this order, printed beside the PO number.
   * Blank when the order has none, and the PDF leaves the row off.
   */
  supplierReference?: string;
  /** The deposit the terms call for, when they name one. */
  depositAmount?: number;
  /** Payments already recorded, oldest first. */
  payments?: PurchaseOrderPayment[];
};

export type PurchaseOrderTotals = {
  discount: number;
  /** Line total per item, index-aligned with the order's line items. */
  lineTotals: number[];
  shipping: number;
  otherCharges: number;
  subtotal: number;
  tax: number;
  taxableAmount: number;
  /**
   * The rate the tax figure was actually calculated at, after clamping. Read
   * this rather than the raw input when labelling the tax line, so the caption
   * can never claim a rate the amount beside it was not charged at.
   */
  taxPercent: number;
  total: number;
  totalQuantity: number;
};

/**
 * Sourced from the shared ISO 4217 set rather than a local list, so a PO can be
 * issued in any currency the rest of the product supports.
 */
export const CURRENCIES = ALL_CURRENCIES.map((entry) => ({
  code: entry.code,
  label: `${entry.code} (${entry.name})`,
}));

export { INCOTERMS } from '~/lib/purchase-orders/incoterms';

/** Money to the cent, exported so the PDF can round its own arithmetic. */
export function roundToCents(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

const roundCurrency = roundToCents;

/**
 * Parses a user-typed amount. Thousands separators are stripped so a pasted
 * "1,000" behaves the same as a typed "1000", and anything unparseable reads
 * as zero rather than propagating NaN through every total on the document.
 */
export function parseAmount(value: string) {
  const parsed = Number.parseFloat(value.replaceAll(',', ''));

  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

export function calculatePurchaseOrderTotals(
  order: PurchaseOrder,
): PurchaseOrderTotals {
  const lineTotals = order.lineItems.map((item) =>
    roundCurrency(parseAmount(item.quantity) * parseAmount(item.unitPrice)),
  );

  const subtotal = roundCurrency(
    lineTotals.reduce((sum, lineTotal) => sum + lineTotal, 0),
  );

  const totalQuantity = order.lineItems.reduce(
    (sum, item) => sum + parseAmount(item.quantity),
    0,
  );

  // A discount larger than the subtotal would produce a negative taxable
  // amount and a negative tax line, so it is capped at the subtotal.
  const discount = Math.min(parseAmount(order.discount), subtotal);
  const taxableAmount = roundCurrency(subtotal - discount);
  const taxPercent = Math.min(100, parseAmount(order.taxPercent));
  const tax = roundCurrency(taxableAmount * (taxPercent / 100));
  const shipping = roundCurrency(parseAmount(order.shippingCost));
  const otherCharges = roundCurrency(parseAmount(order.otherCharges ?? ''));

  return {
    discount: roundCurrency(discount),
    lineTotals,
    otherCharges,
    shipping,
    subtotal,
    tax,
    taxableAmount,
    taxPercent,
    total: roundCurrency(taxableAmount + tax + shipping + otherCharges),
    totalQuantity,
  };
}

/**
 * Drops rows the user never filled in, keeping each remaining item's original
 * index so callers can still read its precomputed line total. The preview and
 * the PDF both go through here — they must never disagree about which rows
 * belong on the document.
 */
export function getVisibleLineItems(order: PurchaseOrder) {
  return order.lineItems
    .map((item, index) => ({ index, item }))
    .filter(
      ({ item }) =>
        item.description.trim() ||
        item.sku.trim() ||
        parseAmount(item.quantity) > 0,
    );
}

export function formatCurrency(value: number, currency: string) {
  return formatMoney({ currency, value });
}

/**
 * Amounts inside the generated PDF are written as "1,234.56" with the currency
 * named once in the column header and totals labels. jsPDF's built-in fonts
 * cover only a single-byte encoding, so avoiding symbols entirely keeps
 * non-Latin currencies from rendering as a wrong glyph on the document.
 */
export function formatAmount(value: number) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);
}

export function formatQuantity(value: number) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value);
}

/** Formats an ISO date (yyyy-mm-dd) for display without a timezone shift. */
export function formatDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());

  if (!match) {
    return value.trim();
  }

  const [, year, month, day] = match;

  const date = new Date(Number(year), Number(month) - 1, Number(day));

  if (Number.isNaN(date.getTime())) {
    return value.trim();
  }

  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Builds a filesystem-safe PDF filename from the PO number. */
export function buildPurchaseOrderFilename(poNumber: string) {
  const slug = poNumber
    .trim()
    .replaceAll(/[^\dA-Za-z-]+/g, '-')
    .replaceAll(/-+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .toLowerCase();

  return `purchase-order-${slug || 'draft'}.pdf`;
}
